import React, { useState, useEffect } from 'react';
import playLocalDTMFTone from '../voip/dtmfTone';
import { useVoip } from '../voip/VoipProvider';
import '../assets/styles/rhinovoip-dialpad.less';

import backspaceButton from '../assets/images/voip/backspace-button.svg';
import callButton from '../assets/images/voip/call-button.svg';
import hangUpButton from '../assets/images/voip/hangup-button.svg';
import micMuteIcon from '../assets/images/voip/mic-mute.svg';
import pauseIcon from '../assets/images/voip/pause.svg';
import transferBlind from '../assets/images/voip/transfer-blind-button.svg';
import transferConsult from '../assets/images/voip/transfer-consult-button.svg';
import rejectButton from '../assets/images/voip/reject-call-button.svg';
import answerButton from '../assets/images/voip/answer-call-button.svg';

const DIGITS = [
  { key: '1', letters: '' },
  { key: '2', letters: 'ABC' },
  { key: '3', letters: 'DEF' },
  { key: '4', letters: 'GHI' },
  { key: '5', letters: 'JKL' },
  { key: '6', letters: 'MNO' },
  { key: '7', letters: 'PQRS' },
  { key: '8', letters: 'TUV' },
  { key: '9', letters: 'WXYZ' },
  { key: '*', letters: '' },
  { key: '0', letters: '+' },
  { key: '#', letters: '' },
];

export default function RhinoVOIPDialPad() {
  const {
    dialedNumber,
    call,
    answer,
    reject,
    hangup,
    toggleMute,
    toggleHold,
    sendDTMF,
    isMuted,
    isHeld,
    callPhase,
    blindTransfer,
    startConsultTransfer,
    completeConsultTransfer,
    cancelConsultTransfer,
    isConsulting,
    isRegistered,
    isSilentMode,
    toggleSilentMode,
    isDndMode,
    toggleDndMode,
  } = useVoip();

  const [number, setNumber] = useState('');
  const [blindTransferMode, setBlindTransferMode] = useState(false);
  const [consultTransferMode, setConsultTransferMode] = useState(false);
  const [transferNotice, setTransferNotice] = useState(null);

  const isIdle = callPhase === 'idle' || callPhase === 'ended';
  const isActive = callPhase === 'active';

  let lastPress = 0;

  useEffect(() => {
    if (callPhase === 'ended') {
      setNumber('');
      setBlindTransferMode(false);
      setConsultTransferMode(false);
      setTransferNotice(null);
    }
  }, [callPhase]);

  useEffect(() => {
    if (callPhase === 'active' && dialedNumber) {
      setNumber(dialedNumber);
    }
  }, [callPhase, dialedNumber]);

  const handleDigit = (digit) => {
    const now = Date.now();
    if (now - lastPress < 100) return;
    lastPress = now;

    playLocalDTMFTone(digit);

    if (isActive && !blindTransferMode && !consultTransferMode && !isConsulting) {
      sendDTMF(digit);
    } else {
      setNumber((prev) => prev + digit);
    }
  };

  const handleBackspace = () => setNumber((prev) => prev.slice(0, -1));
  const handleClear = () => setNumber('');

  const handleHangupClick = () => {
    hangup();
    handleClear();
    setBlindTransferMode(false);
    setConsultTransferMode(false);
  };

  const showTransferNotice = () => {
    setTransferNotice('Transfer sent');
    setTimeout(() => setTransferNotice(null), 2000);
  };

  /* ---------- Blind transfer ---------- */

  const confirmBlindTransfer = () => {
    if (!number.trim()) return;
    blindTransfer(number.trim());
    setBlindTransferMode(false);
    setNumber('');
    showTransferNotice();
  };

  /* ---------- Consult transfer ---------- */

  const startConsult = () => {
    if (!number.trim()) return;
    startConsultTransfer(number.trim());
    setNumber('');
  };

  const cancelConsult = () => {
    cancelConsultTransfer();
    setConsultTransferMode(false);
    setNumber('');
  };

  const completeConsult = () => {
    completeConsultTransfer();
    setConsultTransferMode(false);
    setNumber('');
    showTransferNotice();
  };

  let dialpadPlaceholder = 'Dial Number';
  if (blindTransferMode) {
    dialpadPlaceholder = 'Blind Transfer To...';
  } else if (consultTransferMode) {
    dialpadPlaceholder = 'Consult Transfer To...';
  }

  return (
    <div className="dialpad-container">

      <div className="dialpad-status-bar">
        {/* Phone Status */}
        <button
          type="button"
          className={`dialpad-status-btn ${isRegistered ? 'is-connected' : 'is-disconnected'}`}
          title={isRegistered ? 'Registered' : 'Disconnected'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
        </button>

        {/* Ringer */}
        <button
          type="button"
          className={`dialpad-status-btn ${isSilentMode ? 'is-silent' : ''}`}
          onClick={toggleSilentMode}
          title={isSilentMode ? 'Silent' : 'Ringer'}
        >
          {isSilentMode ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
              <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
              <path d="M18 8a6 6 0 0 0-9.33-5" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          )}
        </button>

        {/* Do Not Disturb */}
        <button
          type="button"
          className={`dialpad-status-btn ${isDndMode ? 'is-dnd' : ''}`}
          onClick={toggleDndMode}
          title={isDndMode ? 'Do Not Disturb' : 'Available'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        </button>
      </div>

      {transferNotice && (
        <div className="transfer-notice">
          {transferNotice}
        </div>
      )}

      <div className="dialpad-display">
        <input
          value={number}
          placeholder={dialpadPlaceholder}
          onChange={(e) => {
            const cleaned = e.target.value.replace(/[^\d*#+]/g, '');
            setNumber(cleaned);
          }}
          inputMode="tel"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />
      </div>

      <div className="dialpad-grid">
        {DIGITS.map(({ key, letters }) => (
          <button
            key={key}
            type="button"
            className="digit-button"
            onClick={() => handleDigit(key)}
          >
            <div className="digit-text">{key}</div>
            {letters && <div className="letters">{letters}</div>}
          </button>
        ))}
      </div>

      <div className="dial-pad-controls">

        {/* Idle */}
        {isIdle && (
          <div className="call-controls">
            <button type="button" className="call-button" onClick={() => call(number)}>
              <img src={callButton} alt="Call" draggable="false" />
            </button>

            <button
              type="button"
              className={`backspace-button ${number.length ? 'visible' : 'hidden'}`}
              onClick={handleBackspace}
            >
              <img src={backspaceButton} alt="Backspace" draggable="false" />
            </button>
          </div>
        )}

        {/* Incoming */}
        {callPhase === 'ringing' && (
          <div className="incoming-controls">
            <button type="button" className="answer-button" onClick={answer}>
              <img src={answerButton} alt="Answer" draggable="false" />
            </button>
            <button type="button" className="reject-button" onClick={reject}>
              <img src={rejectButton} alt="Reject" draggable="false" />
            </button>
          </div>
        )}

        {/* Active – normal */}
        {isActive && !blindTransferMode && !consultTransferMode && !isConsulting && (
          <div className="active-controls grid-two-tier">

            <button type="button" className={`mute-button ${isMuted ? 'active' : ''}`} onClick={toggleMute}>
              <img src={micMuteIcon} alt="Mute" draggable="false" />
            </button>

            <button type="button" className="hangup-button" onClick={handleHangupClick}>
              <img src={hangUpButton} alt="Hang Up" draggable="false" />
            </button>

            <button type="button" className={`hold-button ${isHeld ? 'active' : ''}`} onClick={toggleHold}>
              <img src={pauseIcon} alt="Hold" draggable="false" />
            </button>

            <button
              type="button"
              className="transfer-button blind"
              onClick={() => {
                setNumber('');
                setBlindTransferMode(true);
              }}
            >
              <img src={transferBlind} alt="Blind Transfer" draggable="false" />
            </button>

            <button
              type="button"
              className="transfer-button consult"
              onClick={() => {
                setNumber('');
                setConsultTransferMode(true);
              }}
            >
              <img src={transferConsult} alt="Consult Transfer" draggable="false" />
            </button>

          </div>
        )}

        {/* Blind transfer */}
        {isActive && blindTransferMode && (
          <div className="transfer-controls">
            <button type="button" className="cancel-transfer-button" onClick={() => setBlindTransferMode(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="confirm-transfer-button"
              disabled={!number.trim()}
              onClick={confirmBlindTransfer}
            >
              Transfer
            </button>
          </div>
        )}

        {/* Consult dialing */}
        {isActive && consultTransferMode && !isConsulting && (
          <div className="transfer-controls">
            <button type="button" className="cancel-transfer-button" onClick={cancelConsult}>
              Cancel
            </button>
            <button
              type="button"
              className="confirm-transfer-button"
              disabled={!number.trim()}
              onClick={startConsult}
            >
              Call
            </button>
          </div>
        )}

        {/* Consult active */}
        {isActive && isConsulting && (
          <div className="transfer-controls">
            <button type="button" className="cancel-transfer-button" onClick={cancelConsult}>
              Cancel
            </button>
            <button
              type="button"
              className="confirm-transfer-button"
              onClick={completeConsult}
            >
              Complete Transfer
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
